import { useRef } from 'react';
import { toast } from 'react-toastify';
import MainTable, { MainTableColumn } from '@/components/company/table/MainTable';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import { IJobDescription, IJobDescriptionPaginatedResponse } from '@/states/job.state';

interface RowDraft {
  report?: string;
  file?: File | null;
  isDeleted?: boolean;
}

interface Props {
  isLoading: boolean;
  paginatedData: IJobDescriptionPaginatedResponse;
  pageConfig: DataConfig;
  setPageConfig: React.Dispatch<React.SetStateAction<DataConfig>>;
  drafts: Record<number, RowDraft>;
  onDraftChange: (id: number, patch: Partial<RowDraft>) => void;
  showActions?: boolean;
}

function UploadCell({
  row,
  draft,
  onDraftChange,
  disabled,
}: {
  row: IJobDescription;
  draft: RowDraft | undefined;
  onDraftChange: (id: number, patch: Partial<RowDraft>) => void;
  disabled: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  let existingName: string | null = null;
  if (row.report_file) {
    try {
      const files = JSON.parse(row.report_file);
      if (files.length) {
        const f = files[0];
        existingName = f.original_name || f.filename || f.path?.split('/').pop() || 'File';
      }
    } catch {
      existingName = null;
    }
  }

  const isDeleted = draft?.isDeleted === true;
  const displayName = !isDeleted && draft?.file ? draft.file.name : !isDeleted ? existingName : null;
  const showExistingLink = !isDeleted && !!existingName && !draft?.file;

  let fileUrl = '#';
  if (showExistingLink && row.report_file) {
    try {
      const files = JSON.parse(row.report_file);
      fileUrl = files[0]?.url ?? '#';
    } catch {
      fileUrl = '#';
    }
  }

  let fileLabel: React.ReactNode = 'Belum ada file';
  if (displayName && showExistingLink) {
    fileLabel = (
      <a
        href={fileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 underline hover:text-blue-800"
      >
        {displayName}
      </a>
    );
  } else if (displayName) {
    fileLabel = displayName;
  }

  return (
    <div className="flex items-center gap-2">
      {!disabled && (
        <>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
          >
            Upload
          </button>
          <input
            ref={inputRef}
            type="file"
            accept=".pdf"
            className="hidden"
            onChange={e => {
              const file = e.target.files?.[0] ?? null;
              if (file && row.id != null) {
                if (file.type !== 'application/pdf') {
                  toast.error(`File ${file.name} harus berformat PDF`);
                  e.target.value = '';
                  return;
                }
                if (file.size > 1 * 1024 * 1024) {
                  toast.error(`Ukuran file ${file.name} tidak boleh lebih dari 1 MB`);
                  e.target.value = '';
                  return;
                }
                const patch: Partial<RowDraft> = { file, isDeleted: false };
                const currentReportName = draft?.report ?? row.report ?? '';
                if (!currentReportName.trim()) {
                  patch.report = file.name.replace(/\.[^/.]+$/, '');
                }
                onDraftChange(Number(row.id), patch);
              }
              e.target.value = '';
            }}
          />
        </>
      )}
      <div className="flex items-center gap-1">
        <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[140px]">{fileLabel}</span>
        {!disabled && displayName && (
          <button
            type="button"
            onClick={() => onDraftChange(Number(row.id), { file: null, isDeleted: true })}
            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
            title="Hapus File"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 6h18"></path>
              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

export default function ReportTable({
  isLoading,
  paginatedData,
  pageConfig,
  setPageConfig,
  drafts,
  onDraftChange,
  showActions = true,
}: Props) {
  const columns: MainTableColumn[] = [
    {
      name: 'org_chart_node.name',
      label: 'Jabatan',
      render: (_: unknown, row: IJobDescription) => (
        <span className="font-bold text-gray-800 dark:text-white">{row?.org_chart_node?.name ?? '-'}</span>
      ),
    },
    {
      name: 'name',
      label: 'Uraian Tugas',
      render: (_: unknown, row: IJobDescription) => (
        <span className="text-sm text-gray-700 dark:text-gray-200">{row?.name ?? '-'}</span>
      ),
    },
    {
      name: 'report',
      label: 'Nama Laporan',
      render: (_: unknown, row: IJobDescription) => {
        const id = row.id != null ? Number(row.id) : null;
        const value = (id != null ? drafts[id]?.report : undefined) ?? row.report ?? '';
        return <span className="text-sm text-gray-700 dark:text-gray-200">{value || 'Belum ada laporan'}</span>;
      },
    },
    {
      name: 'frequency',
      label: 'Frekuensi',
      render: (_: unknown, row: IJobDescription) => (
        <span className="font-semibold text-sm text-gray-800 dark:text-gray-100">{row?.frequency ?? '-'}</span>
      ),
    },
    {
      name: 'report_file',
      label: 'Upload Template File',
      sortable: false,
      render: (_: unknown, row: IJobDescription) => {
        const id = row.id != null ? Number(row.id) : null;
        if (id == null) return <span className="text-sm text-gray-400">-</span>;
        return <UploadCell row={row} draft={drafts[id]} onDraftChange={onDraftChange} disabled={!showActions} />;
      },
    },
  ];

  return (
    <MainTable
      showHeader={false}
      isLoading={isLoading}
      data={{
        content: paginatedData.content as unknown as Record<string, unknown>[],
        totalElements: paginatedData.totalElements,
        totalPages: paginatedData.totalPages,
      }}
      columns={columns}
      pageConfig={pageConfig}
      setPageConfig={setPageConfig}
      showActions={false}
      sectionClassName="px-5"
    />
  );
}
